import {TransactionWebPage} from '@fe-progressive/package-progressive';
import type {
  ProgressiveTransactionAdapter,
  ProgressiveTransactionFetchInput,
  ProgressiveTransactionItem,
  ProgressiveTransactionRiskFetchInput,
} from '@fe-progressive/package-progressive/transaction';
import globalStore from '@/services/global.state';
import {copyText, goBack, toPriceStr} from '@/utils';
import dayjs from 'dayjs';
import React from 'react';
import './transaction-progressive.scss';
import {
  getList,
  getRiskList,
  getTabs,
} from '../transaction/transaction-service';

function formatAmount(amount: number) {
  return toPriceStr(amount, {
    thousands: true,
    fixed: 2,
    spacing: true,
    currency: globalStore.currency,
  });
}

function mapTransactionItem(item: any): ProgressiveTransactionItem {
  return {
    ...item,
    id: item.order,
    appDate: item.appDate,
    balance: item.balance,
    changeType: item.changeType,
    gameName: item.gameName,
    iconUrl: item.iconUrl,
    order: item.order,
    payType: item.payType,
    payTypeName: item.payTypeName,
    price: item.price,
    reward: item.reward,
    walletDetailStatus: item.walletDetailStatus,
    raw: item,
  };
}

const TRANSACTION_PROGRESSIVE_VIEWPORT_HEIGHT_VAR =
  '--transaction-progressive-viewport-height';

function useTransactionProgressiveViewportHeightStyle() {
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const updateViewportHeight = () => {
      const height = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty(
        TRANSACTION_PROGRESSIVE_VIEWPORT_HEIGHT_VAR,
        `${height}px`,
      );
    };

    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('resize', updateViewportHeight);
    window.visualViewport?.addEventListener('scroll', updateViewportHeight);

    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.visualViewport?.removeEventListener(
        'resize',
        updateViewportHeight,
      );
      window.visualViewport?.removeEventListener(
        'scroll',
        updateViewportHeight,
      );
      document.documentElement.style.removeProperty(
        TRANSACTION_PROGRESSIVE_VIEWPORT_HEIGHT_VAR,
      );
    };
  }, []);

  return React.useMemo(
    () => ({
      height: `var(${TRANSACTION_PROGRESSIVE_VIEWPORT_HEIGHT_VAR}, 100vh)`,
    }),
    [],
  );
}

export default function TransactionsProgressive() {
  const [amount, setAmount] = React.useState(0);
  const viewportHeightStyle = useTransactionProgressiveViewportHeightStyle();

  React.useEffect(() => {
    const sub = globalStore.amountChanged.subscribe(res => {
      setAmount(res.current);
    });
    return () => {
      sub.unsubscribe();
    };
  }, []);

  const adapter = React.useMemo<ProgressiveTransactionAdapter>(
    () => ({
      fetchTabs: async () =>
        (await getTabs()).map(tab => ({
          type: tab.type,
          typeName: tab.typeName,
          hide: Boolean(tab.hide?.length),
        })),
      fetchTransactions: async (input: ProgressiveTransactionFetchInput) => {
        const res = await getList({
          pageNo: input.pageNo,
          pageSize: input.pageSize,
          changeDesc: String(input.tabType),
          yearMonth: input.yearMonth,
        });
        return res.map(mapTransactionItem);
      },
      fetchRiskTransactions: async (
        input: ProgressiveTransactionRiskFetchInput,
      ) => {
        const res = await getRiskList({
          pageNo: input.pageNo,
          changeDesc: input.tabType,
          yearMonth: input.yearMonth,
          status: input.riskStatus,
        });
        return res.map(mapTransactionItem);
      },
      copyText: (text: string) => copyText(text),
      showToast: notice => globalStore.globalSucessTotal(notice.message),
    }),
    [],
  );

  return (
    <div className="transaction-progressive-page" style={viewportHeightStyle}>
      <TransactionWebPage
        adapter={adapter}
        amount={amount}
        onBack={goBack}
        formatAmount={formatAmount}
        formatTime={(time?: number) =>
          time ? dayjs(Number(time)).format('DD/MM/YYYY hh:mm') : ''
        }
      />
    </div>
  );
}
